import React, {useState} from 'react';
import {useQuery} from "react-query";
import ngage from "services/ngage";
import Page from "components/Page";
import {Table, TableBody, TableData, TableHead, TableHeader, TableRow} from "components/Table";
import {prefetchNearbyPages} from "services/query-client";
import {QrcodeIcon} from "@heroicons/react/outline";
import {formattedDateTime} from "services/date";
import Button from "components/Button";
import {EyeIcon, PencilIcon, PlusIcon, TrashIcon} from "@heroicons/react/solid";
import Dropdown from "components/Dropdown";
import {QrCode} from "types/QrCode";
import {useNotifications} from "context/NotificationContext";
import {useNavigate} from "react-router-dom";
import DeleteQrCode from '../Delete';
import BooleanIndicator from "../../../components/BooleanIndicator";

function QrCodeList() {
  const [page, setPage] = useState<number>(1);
  const result = useQuery(['qr-codes.all', {page}], () => ngage.qrCodes.all(page), {keepPreviousData: true})

  const [deletingQrCode, setDeletingQrCode] = useState<QrCode | null>(null);

  const {notify} = useNotifications()
  const navigate = useNavigate()

  // Prefetch next and previous pages
  prefetchNearbyPages('qr-codes.all', page, ngage.qrCodes.all)

  // Prepare page actions
  const actions = <Button icon={PlusIcon} onClick={() => navigate('/qr-codes/new')}>New</Button>

  return (
    <Page
      title="QR Codes"
      subTitle={`Customisable QR codes for interactive engagement`}
      actions={actions}
      breadcrumbs={{'Operations': '/', 'QR Codes': '/qr-codes'}}
    >
      <Table paginationData={result.data} setPage={setPage}>
        <TableHead>
          <TableRow>
            <TableHeader>Name</TableHeader>
            <TableHeader alignment={TableHeader.alignments.CENTER}>Branded</TableHeader>
            <TableHeader alignment={TableHeader.alignments.CENTER}>Enabled</TableHeader>
            <TableHeader>Added</TableHeader>
            <TableHeader/>
          </TableRow>
        </TableHead>

        <TableBody isLoading={result.isLoading}>

          {result?.data && result.data.data.length < 1 ?
            <TableRow index={0}>
              <TableData colSpan={5}>
                <div className="py-4 flex flex-col justify-center items-center">
                  <QrcodeIcon className="h-8 w-8 text-gray-400 mb-2"/>
                  <span className="text-sm font-medium text-gray-600">No QR Codes</span>
                </div>
              </TableData>
            </TableRow>
            : result?.data?.data.map((qrCode, index) => (
              <TableRow key={qrCode.id} index={index} highlight>
                <TableData>
                  <div className="flex">
                    <div className="items-center absolute h-full left-[0.35rem] top-0 flex duration-200 transition-opacity group-hover:opacity-100 opacity-0">
                      {/* TODO: Convert to component */}
                      <Dropdown position='left'>
                        {[
                          {name: 'View', icon: EyeIcon, onClick: () => navigate(`/qr-codes/${qrCode.id}`)},
                          // {name: 'Test', icon: CursorClickIcon, onClick: () => window.open(qrCode.url)}, # TODO: Re-enable this when QR code testing is ready
                          {name: 'Edit', icon: PencilIcon, onClick: () => navigate(`/qr-codes/${qrCode.id}/edit`)},
                          {
                            name: 'Delete',
                            icon: TrashIcon,
                            onClick: () => setDeletingQrCode(qrCode),
                            disabled: qrCode.trigger_count > 0
                          }
                        ]}
                      </Dropdown>
                    </div>

                    <div className="flex group-hover:translate-x-1 transition-all">
                      <QrcodeIcon className="h-5 w-5 mr-2 text-gray-500"/> {qrCode.name}
                    </div>
                  </div>
                </TableData>
                <TableData alignment={TableData.alignments.CENTER}>
                  <BooleanIndicator value={qrCode.appearance.is_branded} />
                </TableData>
                <TableData alignment={TableData.alignments.CENTER}>
                  <BooleanIndicator value={qrCode.is_enabled} />
                </TableData>
                <TableData>{formattedDateTime(qrCode.created_at)}</TableData>
                <TableData alignment={TableData.alignments.RIGHT}>
                  {/* TODO: Convert to component */}
                  <Dropdown>
                    {[
                      {name: 'View', icon: EyeIcon, onClick: () => navigate(`/qr-codes/${qrCode.id}`)},
                      // {name: 'Test', icon: CursorClickIcon, onClick: () => window.open(qrCode.url)}, # TODO: Re-enable this when QR code testing is ready
                      {name: 'Edit', icon: PencilIcon, onClick: () => navigate(`/qr-codes/${qrCode.id}/edit`)},
                      {
                        name: 'Delete',
                        icon: TrashIcon,
                        onClick: () => setDeletingQrCode(qrCode),
                        disabled: qrCode.trigger_count > 0
                      }
                    ]}
                  </Dropdown>
                </TableData>

                {/* Delete confirmation modal */}
                <DeleteQrCode
                  qrCode={deletingQrCode}
                  show={qrCode === deletingQrCode}
                  onCancel={() => setDeletingQrCode(null)}
                  onDelete={qrCode => {
                    navigate('/qr-codes')
                    notify("success", "QR Code Deleted", `${qrCode.name} has been permanently removed`)
                  }}
                  onDeleteFail={qrCode => {
                    setDeletingQrCode(null)
                    notify("error", "QR Code Not Deleted", `${qrCode.name} could not be removed`)
                  }}
                />
              </TableRow>
            ))}

        </TableBody>
      </Table>
    </Page>
  );
}

export default QrCodeList;